/**
 * Shared technician day picker: week rail + prev/next week nav + a pop-up month
 * calendar for jumping to any date. Used by the Job List and Time Sheet so both
 * screens browse one day at a time with identical look & behaviour.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function startOfDay(date: Date): Date {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

export function addDays(date: Date, n: number): Date {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

/** Monday-anchored start of the week containing `date`. */
export function startOfWeek(date: Date): Date {
    const d = startOfDay(date);
    const day = d.getDay();
    const diff = day === 0 ? 6 : day - 1;
    return addDays(d, -diff);
}

export function sameDay(a: Date, b: Date): boolean {
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export function dayKey(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
}

type Props = {
    selectedDay: Date;
    onSelectDay: (day: Date) => void;
    /** Map of dayKey -> item count, drives the badge on the rail and the dot in the calendar. */
    counts?: Map<string, number>;
};

export default function TechDaySelector({ selectedDay, onSelectDay, counts }: Props): React.JSX.Element {
    const today = useMemo(() => startOfDay(new Date()), []);
    const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(selectedDay));
    const [calOpen, setCalOpen] = useState(false);
    const [calMonth, setCalMonth] = useState<Date>(() => new Date(selectedDay.getFullYear(), selectedDay.getMonth(), 1));
    const navRef = useRef<HTMLDivElement>(null);

    // Keep the visible week in sync if the selected day is moved outside it externally.
    useEffect(() => {
        const ws = startOfWeek(selectedDay);
        setWeekStart((cur) => (sameDay(cur, ws) ? cur : ws));
    }, [selectedDay]);

    useEffect(() => {
        if (!calOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (navRef.current && !navRef.current.contains(e.target as Node)) setCalOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [calOpen]);

    const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

    const weekLabel = useMemo(() => {
        const end = addDays(weekStart, 6);
        const sameMonth = weekStart.getMonth() === end.getMonth();
        return sameMonth
            ? `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()}–${end.getDate()}`
            : `${MONTHS[weekStart.getMonth()]} ${weekStart.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}`;
    }, [weekStart]);

    const calCells = useMemo(() => {
        const monthStart = new Date(calMonth.getFullYear(), calMonth.getMonth(), 1);
        const gridStart = startOfWeek(monthStart);
        return Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
    }, [calMonth]);

    function openCalendar() {
        setCalMonth(new Date(selectedDay.getFullYear(), selectedDay.getMonth(), 1));
        setCalOpen((o) => !o);
    }

    function goToday() {
        setWeekStart(startOfWeek(today));
        setCalMonth(new Date(today.getFullYear(), today.getMonth(), 1));
        setCalOpen(false);
        onSelectDay(today);
    }

    function selectFromCalendar(d: Date) {
        const day = startOfDay(d);
        setWeekStart(startOfWeek(day));
        setCalOpen(false);
        onSelectDay(day);
    }

    return (
        <>
            <div className="tech-daynav" ref={navRef}>
                <button
                    type="button"
                    className="tech-daynav__arrow"
                    aria-label="Previous week"
                    onClick={() => setWeekStart((w) => addDays(w, -7))}
                >
                    ‹
                </button>
                <button
                    type="button"
                    className="tech-daynav__label"
                    aria-haspopup="dialog"
                    aria-expanded={calOpen}
                    onClick={openCalendar}
                >
                    {weekLabel}
                    <span className="tech-daynav__caret" aria-hidden="true">▾</span>
                </button>
                <button
                    type="button"
                    className="tech-daynav__arrow"
                    aria-label="Next week"
                    onClick={() => setWeekStart((w) => addDays(w, 7))}
                >
                    ›
                </button>

                {calOpen ? (
                    <div className="tech-cal" role="dialog" aria-label="Pick a date">
                        <div className="tech-cal__head">
                            <button
                                type="button"
                                className="tech-cal__nav"
                                aria-label="Previous month"
                                onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1))}
                            >
                                ‹
                            </button>
                            <span className="tech-cal__title">
                                {MONTHS[calMonth.getMonth()]} {calMonth.getFullYear()}
                            </span>
                            <button
                                type="button"
                                className="tech-cal__nav"
                                aria-label="Next month"
                                onClick={() => setCalMonth((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1))}
                            >
                                ›
                            </button>
                        </div>
                        <div className="tech-cal__dow">
                            {['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'].map((d) => (
                                <span key={d}>{d}</span>
                            ))}
                        </div>
                        <div className="tech-cal__grid">
                            {calCells.map((d) => {
                                const inMonth = d.getMonth() === calMonth.getMonth();
                                const isSel = sameDay(d, selectedDay);
                                const isToday = sameDay(d, today);
                                const hasItems = (counts?.get(dayKey(d)) || 0) > 0;
                                return (
                                    <button
                                        key={dayKey(d)}
                                        type="button"
                                        className={[
                                            'tech-cal__cell',
                                            inMonth ? '' : 'is-out',
                                            isSel ? 'is-active' : '',
                                            isToday ? 'is-today' : ''
                                        ].filter(Boolean).join(' ')}
                                        onClick={() => selectFromCalendar(d)}
                                    >
                                        {d.getDate()}
                                        {hasItems ? <span className="tech-cal__dot" aria-hidden="true" /> : null}
                                    </button>
                                );
                            })}
                        </div>
                        <button type="button" className="tech-cal__today" onClick={goToday}>
                            Jump to today
                        </button>
                    </div>
                ) : null}
            </div>

            <div className="tech-dayrail" role="tablist" aria-label="Select day">
                {weekDays.map((d) => {
                    const count = counts?.get(dayKey(d)) || 0;
                    const isActive = sameDay(d, selectedDay);
                    const isToday = sameDay(d, today);
                    return (
                        <button
                            key={dayKey(d)}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            className={[
                                'tech-day-pill',
                                isActive ? 'is-active' : '',
                                isToday ? 'is-today' : ''
                            ].filter(Boolean).join(' ')}
                            onClick={() => onSelectDay(startOfDay(d))}
                        >
                            <span className="tech-day-pill__dow">{DOW[d.getDay()]}</span>
                            <span className="tech-day-pill__num">{d.getDate()}</span>
                            {count > 0 ? (
                                <span className="tech-day-pill__count">{count}</span>
                            ) : (
                                <span className="tech-day-pill__count tech-day-pill__count--empty" aria-hidden="true" />
                            )}
                        </button>
                    );
                })}
            </div>
        </>
    );
}
